import React from "react";

import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CircleDashed,
  Eye,
  FileCheck,
  FileText,
  Hourglass,
  Loader2,
} from "lucide-react";

import { CoverageChips } from "./BriefingParts";
import { FLOW, PlanRef, TEAM_STEP } from "./flowCopy";
import { awaitingConfirm } from "./planBucket";
import PlanStateLabel from "./PlanStateLabel";
import PortfolioModal from "./PortfolioModal";
import { shortDate, underReviewFor } from "./shared";
import type { AccountPlanStatus, Persona } from "../../Types/AccountPlan";

/**
 * A row as a sponsor surface shows it: the version the sponsor reads (the
 * newest one with a briefing) laid over the row, so a newer upload whose
 * briefing isn't generated yet doesn't hide the one they had (F-44 = B,
 * 2026-10-19). With no version to read there is no Draft/Published label, and
 * an upload the AE hasn't confirmed isn't there at all: a sponsor never sees
 * one (D8, F-UF-E15). Idempotent.
 */
export const forSponsor = (
  s?: AccountPlanStatus
): AccountPlanStatus | undefined => {
  // A payload from before the sponsor slice existed has no `sponsor` key at all.
  if (!s || s.sponsor === undefined) return s;
  if (s.sponsor) return { ...s, ...s.sponsor };
  const unconfirmed = !!s.latest_file && s.latest_file.confirmed === false;
  return {
    ...s,
    view_state: null,
    view_published_at: null,
    ...(unconfirmed
      ? {
          latest_file: null,
          viewer_ready: false,
          current_file_id: null,
          sponsor_view_status: null,
          top_risks: 0,
          high_risks: 0,
          briefing_summary: null,
        }
      : {}),
  };
};

/** A briefing exists: the newest version has one, or an older one was published
 * (a newer upload doesn't hide the published briefing, F-45). */
export const hasBriefing = (s?: AccountPlanStatus): boolean =>
  !!s?.published || (!!s?.review_status && s.review_status !== "none");

/**
 * The newest plan failed, is still being read, or stopped at a plan check
 * (rejected, or waiting for the AE's OK, F-UF-F08) and there is no briefing to
 * fall back on: the only useful step is Plan (re-upload, fix, watch progress).
 */
const STOPPED: string[] = ["failed", "processing", "rejected", "needs_review"];
const planNotReadable = (s?: AccountPlanStatus): boolean =>
  !hasBriefing(s) && STOPPED.includes(s?.latest_file?.status || "");

/** The tab Quick look's "Go to <step>" opens for an AE/CSM. */
export const currentStep = (
  s?: AccountPlanStatus
): "plan" | "viewer" | "sponsor" | "publish" => {
  if (!s?.latest_file || planNotReadable(s)) return "plan";
  // Read, waiting on the AE's Confirm plan (Phase 2).
  if (awaitingConfirm(s)) return "viewer";
  return s.review_status === "published" ? "publish" : "sponsor";
};

// The step names are the plan page's own (F-UF-F21).
const STEP_LABEL = TEAM_STEP;

/** Label of the step `/account-plan/:id` lands an AE/CSM on (mirrors the page's `defaultTab`). */
export const currentStepLabel = (s?: AccountPlanStatus): string =>
  STEP_LABEL[currentStep(s)];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const btnGhost =
  "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";
const btnPrimary =
  "inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

interface Props {
  account: { id: string; name: string } | null;
  status?: AccountPlanStatus;
  persona: Persona;
  /** Only admins also hold ES, so only they can preview as the sponsor. */
  admin: boolean;
  onClose: () => void;
  /** Open the account's briefing as the sponsor. */
  openBriefing: (id: string) => void;
  /** Open the account at its current step for this persona. */
  openCurrent: (id: string) => void;
  /** AE/CSM: read-only "preview as sponsor" of the account's briefing. */
  openPreview?: (id: string) => void;
  /** AE/CSM: the briefing's own page, read-only (once it is published). */
  openReading?: (id: string) => void;
  /**
   * False for an account the viewer isn't on (Plans → All accounts): the plan page
   * won't let them in, so the Quick look offers no way there, only Close.
   */
  canOpen?: boolean;
}

/** Mock v4 "Quick look": what the sponsor sees, without leaving the list. */
const PortfolioQuickLook: React.FC<Props> = ({
  account,
  status,
  persona,
  admin,
  onClose,
  openBriefing,
  openCurrent,
  openPreview,
  openReading,
  canOpen = true,
}) => {
  const isES = persona === "ES";
  // The sponsor's Quick look summarises the version they read (F-44 = B).
  const s = isES ? forSponsor(status) : status;
  const briefing = hasBriefing(s);
  // What the sponsor reads is the newest usable version: say whether it is a
  // draft or published (Q1 = C + A, 2026-10-19).
  const esState = isES && s?.viewer_ready ? s.view_state || null : null;
  let labelInBody = false;

  let body: React.ReactNode;
  if (s?.latest_file && !briefing && planNotReadable(s)) {
    const st = s.latest_file.status;
    const failed = st !== "processing";
    const ref = PlanRef(s.latest_file);
    const isAE = persona === "AE";
    const text =
      st === "processing"
        ? `${ref} is being read. The briefing can be generated once it's done.`
        : st === "failed"
          ? `${ref} couldn't be read, so there is no briefing yet. ${
              isAE
                ? `Upload it again from the ${TEAM_STEP.plan} step.`
                : "The AE can upload it again."
            }`
          : st === "rejected"
            ? `${ref} didn't pass the plan checks, so there is no briefing yet. ${
                isAE
                  ? `Fix it from the ${TEAM_STEP.plan} step.`
                  : "The AE can upload a fixed file."
              }`
            : `${ref} needs ${isAE ? "your" : "the AE's"} OK before the briefing is written.`;
    const Icon = failed ? AlertTriangle : Loader2;
    body = (
      <div
        className={`rounded-lg border p-5 text-center text-sm ${
          failed
            ? "border-amber-200 bg-amber-50 text-amber-900"
            : "border-dashed border-gray-300 bg-gray-50 text-gray-600"
        }`}
      >
        <Icon
          className={`mx-auto mb-2 h-5 w-5 ${
            failed ? "text-amber-600" : "animate-spin text-[#974ae4]"
          }`}
          aria-hidden
        />
        {text}
      </div>
    );
  } else if (!s?.latest_file || !briefing) {
    body = (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-600">
        {s?.latest_file ? (
          <FileCheck
            className="mx-auto mb-2 h-5 w-5 text-gray-400"
            aria-hidden
          />
        ) : (
          <CircleDashed
            className="mx-auto mb-2 h-5 w-5 text-gray-400"
            aria-hidden
          />
        )}
        {s?.latest_file
          ? `${PlanRef(s.latest_file)} ingested. No briefing has been generated yet.`
          : "No account plan has been ingested yet, so there is no briefing to show."}
      </div>
    );
  } else {
    const published = s.review_status === "published" && s.published;
    const risks = s.high_risks || 0;
    const summary = s.briefing_summary || null;
    labelInBody = !!esState;
    body = (
      <>
        {published ? (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {esState && <PlanStateLabel state="published" showDate={false} />}
            <BadgeCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              Reviewed and published
              {s.published?.by_name ? ` by ${s.published.by_name}` : ""} ·{" "}
              {shortDate(s.published?.at)}
            </span>
          </div>
        ) : esState ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <PlanStateLabel state="draft" variant="banner" />
          </div>
        ) : (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <Hourglass className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              {s.review_started_at
                ? underReviewFor(s.review_started_at)
                : "Draft briefing generated · review not started"}
            </span>
          </div>
        )}
        {summary?.bottom_line && (
          <>
            <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Bottom line
            </div>
            <p className="mb-4 text-sm leading-relaxed text-gray-800">
              {summary.bottom_line}
            </p>
          </>
        )}
        <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
          Top risks
        </div>
        {summary ? (
          summary.top_risks.length ? (
            <ul className="mb-4 space-y-1.5">
              {summary.top_risks.map(r => (
                <li
                  key={r.question_id}
                  className="flex gap-2 text-sm text-gray-800"
                >
                  <span className="h-fit shrink-0 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700">
                    Top risk
                  </span>
                  <span>
                    {r.direct_answer}{" "}
                    <span className="text-gray-500">— {r.question}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-4 text-sm text-gray-500">No top risks flagged.</p>
          )
        ) : (
          <p className="mb-4 text-sm text-gray-800">
            {risks > 0 ? (
              <>
                <span className="font-semibold text-rose-700">
                  {plural(risks, "high-severity risk")}
                </span>{" "}
                in the briefing
              </>
            ) : (
              "No high-severity risks flagged."
            )}
          </p>
        )}
        {summary?.coverage && (
          <>
            <div className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {FLOW.briefingQuestions}
              {summary.plan_version != null
                ? ` · plan v${summary.plan_version}`
                : ""}
              {summary.plan_date ? ` (${shortDate(summary.plan_date)})` : ""}
            </div>
            <div className="rounded-lg border border-gray-200 px-3 py-2.5">
              <CoverageChips coverage={summary.coverage} />
            </div>
          </>
        )}
      </>
    );
  }

  const act = (fn: (id: string) => void) => () => {
    if (!account) return;
    onClose();
    fn(account.id);
  };

  const footer = !canOpen ? (
    <>
      <span className="mr-auto self-center text-xs text-gray-500">
        Only the account&apos;s team can open the plan itself.
      </span>
      <button type="button" onClick={onClose} className={btnPrimary}>
        Close
      </button>
    </>
  ) : isES ? (
    briefing ? (
      <button type="button" onClick={act(openBriefing)} className={btnPrimary}>
        <FileText className="h-4 w-4" aria-hidden /> Open briefing
      </button>
    ) : (
      <button type="button" onClick={onClose} className={btnPrimary}>
        Close
      </button>
    )
  ) : s?.review_status === "published" ? (
    // Published: Publish is done, so there's no step to go to. Open the
    // briefing itself — what the sponsor was given (Sushant, 2026-10-14).
    <button
      type="button"
      onClick={act(openReading || openCurrent)}
      className={btnPrimary}
    >
      <FileText className="h-4 w-4" aria-hidden /> Open published plan
    </button>
  ) : (
    <>
      {briefing && (admin || openPreview) && (
        <button
          type="button"
          onClick={act(admin ? openBriefing : openPreview || openBriefing)}
          className={btnGhost}
        >
          <Eye className="h-4 w-4" aria-hidden /> Preview as sponsor
        </button>
      )}
      <button type="button" onClick={act(openCurrent)} className={btnPrimary}>
        <ArrowRight className="h-4 w-4" aria-hidden /> Go to{" "}
        {currentStepLabel(s)}
      </button>
    </>
  );

  return (
    <PortfolioModal
      open={!!account}
      onClose={onClose}
      eyebrow="Quick look"
      title={account?.name || ""}
      footer={footer}
    >
      {!isES && (
        <div className="mb-4 flex items-center gap-1.5 rounded-md border border-purple-100 bg-purple-50 px-3 py-1.5 text-xs text-[#8538d4]">
          <Eye className="h-3.5 w-3.5" aria-hidden /> This is what{" "}
          {s?.sponsor_name ? `${s.sponsor_name} (sponsor)` : "the sponsor"} sees
        </div>
      )}
      {esState && !labelInBody && (
        <div className="mb-3">
          <PlanStateLabel state={esState} publishedAt={s?.view_published_at} />
        </div>
      )}
      {body}
    </PortfolioModal>
  );
};

export default PortfolioQuickLook;
